import React from "react";
import { FieldArray, FastField } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  TextField,
  FormControlLabel,
  Icon,
} from "@mui/material";

const PLACEHOLDER_CONTPAQI_ACCOUNT_NO = "0000-0000-0000";

const isPlaceholderAccount = (account) =>
  account?.contpaqiAccountNo === PLACEHOLDER_CONTPAQI_ACCOUNT_NO;

const canEditContpaqiAccount = (account, formMode) =>
  formMode === "insert" || !account?.id || isPlaceholderAccount(account);

const ProviderAccountList = ({
  values,
  errors,
  touched,
  setFieldValue,
  formMode,
}) => {
  const handleDefaultChange = (index) => {
    values.accounts.forEach((account, idx) => {
      setFieldValue(`accounts[${idx}].isDefault`, idx === index);
    });
  };

  const formatAccountNumber = (value, index) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");
    
    // If we have 12 digits, format as XXXX-XXXX-XXXX
    if (digitsOnly.length === 12) {
      const formatted = `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 8)}-${digitsOnly.slice(8, 12)}`;
      setFieldValue(`accounts[${index}].contpaqiAccountNo`, formatted);
      return formatted;
    }
    
    // If we have less than 12 digits, allow partial input
    // Format as user types (add hyphens at positions 4 and 8)
    if (digitsOnly.length >= 0) {
      let formatted = digitsOnly;
      if (digitsOnly.length > 4) {
        formatted = `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4)}`;
      }
      if (digitsOnly.length > 8) {
        formatted = `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 8)}-${digitsOnly.slice(8)}`;
      }
      setFieldValue(`accounts[${index}].contpaqiAccountNo`, formatted);
      return formatted;
    }
    
    return value;
  };

  const handleAccountNumberChange = (event, index) => {
    const value = event.target.value;
    formatAccountNumber(value, index);
  };

  return (
    <Grid container pl={2} spacing={2}>
      {formMode === "insert" && values.accounts.length === 0 && (
        <Grid item xs={12}>
          <Alert severity="info">
            Si no ingresa cuentas contables, se creará una cuenta pendiente por
            definir (0000-0000-0000).
          </Alert>
        </Grid>
      )}
      <FieldArray name="accounts">
        {({ push, remove }) => (
          <Box width="100%">
            {values.accounts.map((account, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={4}>
                  <FastField
                    name={`accounts[${index}].contpaqiAccountNo`}
                    as={TextField}
                    label="Cuenta Contable"
                    fullWidth
                    disabled={!canEditContpaqiAccount(account, formMode)}
                    onChange={(event) => handleAccountNumberChange(event, index)}
                    error={
                      touched.accounts?.[index]?.contpaqiAccountNo &&
                      !!errors.accounts?.[index]?.contpaqiAccountNo
                    }
                    helperText={
                      touched.accounts?.[index]?.contpaqiAccountNo &&
                      errors.accounts?.[index]?.contpaqiAccountNo
                    }
                  />
                </Grid>
                <Grid item xs={5}>
                  <FastField
                    name={`accounts[${index}].description`}
                    as={TextField}
                    label="Descripción"
                    fullWidth
                    error={
                      touched.accounts?.[index]?.description &&
                      !!errors.accounts?.[index]?.description
                    }
                    helperText={
                      touched.accounts?.[index]?.description &&
                      errors.accounts?.[index]?.description
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={account.isDefault}
                        onChange={() => handleDefaultChange(index)}
                      />
                    }
                    label="Predeterminado"
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton onClick={() => remove(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              fullWidth
              type="button"
              onClick={() =>
                push({
                  contpaqiAccountNo: "",
                  description: "",
                  isDefault: false,
                })
              }
              variant="contained"
              sx={{ mt: 2 }}
            >
              <Icon>add</Icon>
            </Button>
          </Box>
        )}
      </FieldArray>
    </Grid>
  );
};

export default ProviderAccountList;
